import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import { Warning as WarningIcon, CheckCircle as CheckIcon, Error as ErrorIcon } from '@mui/icons-material';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';

const ViabilityPredictionDashboard = () => {
  const axiosPrivate = useAxiosPrivate();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [modelMetrics, setModelMetrics] = useState(null);
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [historicalValidation, setHistoricalValidation] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [companies, setCompanies] = useState([]);

  const fetchCompanies = async () => {
    try {
      const response = await axiosPrivate.get('/Company');
      if (response.data && response.data.length > 0) {
        setCompanies(response.data);

        if (!selectedCompanyId) {
          setSelectedCompanyId(response.data[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to load companies', err);
      setError('Failed to load companies list');
    }
  };

  const fetchPrediction = async (companyId) => {
    if (!companyId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await axiosPrivate.get(`/CompanyViability/predict/${companyId}`);
      setPrediction(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load prediction');
    } finally {
      setLoading(false);
    }
  };

  const fetchModelMetrics = async () => {
    try {
      const response = await axiosPrivate.get('/CompanyViability/model-metrics');
      setModelMetrics(response.data);
    } catch (err) {
      console.error('Failed to load model metrics', err);
    }
  };

  const fetchDashboardSummary = async () => {
    try {
      const response = await axiosPrivate.get('/CompanyViability/dashboard-summary');
      setDashboardSummary(response.data);
    } catch (err) {
      console.error('Failed to load dashboard summary', err);
    }
  };

  const fetchHistoricalValidation = async () => {
    try {
      const response = await axiosPrivate.get('/CompanyViability/validate-historical', {
        params: { startYear: 2015, endYear: 2020 }
      });
      setHistoricalValidation(response.data);
    } catch (err) {
      console.error('Failed to load historical validation', err);
    }
  };

  useEffect(() => {
    fetchCompanies();
    fetchModelMetrics();
    fetchDashboardSummary();
    fetchHistoricalValidation();
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      fetchPrediction(selectedCompanyId);
    }
  }, [selectedCompanyId]);

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'HIGH': return '#d32f2f';
      case 'MEDIUM': return '#f57c00';
      case 'LOW': return '#388e3c';
      default: return '#757575';
    }
  };

  const getRiskIcon = (risk) => {
    switch (risk) {
      case 'HIGH': return <ErrorIcon />;
      case 'MEDIUM': return <WarningIcon />;
      case 'LOW': return <CheckIcon />;
      default: return null;
    }
  };

  if (loading && !prediction) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Typography variant="h4" gutterBottom>
          🔬 Company Viability Prediction System
        </Typography>
        <Typography variant="subtitle1">
          Survival Analysis & Machine Learning for EDA Company Acquisition Forecasting
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.9 }}>
          PhD Research: Methods for Evaluating Viability of EDA Companies in Times of Technological Disruption
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {}
      {dashboardSummary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card sx={{ background: '#e3f2fd' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Total Companies Analyzed</Typography>
                <Typography variant="h4">{dashboardSummary.totalCompaniesAnalyzed}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ background: '#ffebee' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>High Risk</Typography>
                <Typography variant="h4" sx={{ color: '#d32f2f' }}>{dashboardSummary.highRiskCount}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ background: '#fff3e0' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Medium Risk</Typography>
                <Typography variant="h4" sx={{ color: '#f57c00' }}>{dashboardSummary.mediumRiskCount}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ background: '#e8f5e9' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Low Risk</Typography>
                <Typography variant="h4" sx={{ color: '#388e3c' }}>{dashboardSummary.lowRiskCount}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {}
      {companies.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Select Company for Analysis</InputLabel>
            <Select
              value={selectedCompanyId || ''}
              onChange={(e) => setSelectedCompanyId(e.target.value)}
              label="Select Company for Analysis"
            >
              {companies.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.foundCompName} (ID: {company.id})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      )}

      {}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="fullWidth">
          <Tab label="Company Prediction" />
          <Tab label="Survival Curve" />
          <Tab label="Feature Importance" />
          <Tab label="Model Performance" />
          <Tab label="Historical Validation" />
        </Tabs>
      </Paper>

      {}
      {activeTab === 0 && prediction && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>{prediction.companyName}</Typography>
                <Typography color="textSecondary" gutterBottom>Company ID: {prediction.companyId}</Typography>

                <Box sx={{ my: 2 }}>
                  <Chip
                    label={`${prediction.riskCategory} RISK`}
                    icon={getRiskIcon(prediction.riskCategory)}
                    sx={{
                      background: getRiskColor(prediction.riskCategory),
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      px: 2,
                      py: 3
                    }}
                  />
                </Box>

                <Box sx={{ my: 3 }}>
                  <Typography variant="body2" color="textSecondary">Survival Probability (5-year)</Typography>
                  <Typography variant="h3" sx={{ color: getRiskColor(prediction.riskCategory) }}>
                    {(prediction.survivalProbability * 100).toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Probability of remaining independent beyond 5 years
                  </Typography>
                </Box>

                <Box sx={{ my: 3 }}>
                  <Typography variant="body2" color="textSecondary">Predicted Time to Acquisition</Typography>
                  <Typography variant="h3">
                    {prediction.predictedTimeToAcquisition.toFixed(1)} years
                  </Typography>
                </Box>

                <Box sx={{ my: 2 }}>
                  <Typography variant="body2" color="textSecondary">Model Confidence</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <Box sx={{
                        width: `${prediction.confidenceScore * 100}%`,
                        height: 20,
                        background: '#4caf50',
                        borderRadius: 1
                      }} />
                    </Box>
                    <Typography variant="body2">{(prediction.confidenceScore * 100).toFixed(0)}%</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>⚠️ Risk Factors</Typography>
                {prediction.riskFactors.length > 0 ? (
                  prediction.riskFactors.map((factor, idx) => (
                    <Alert key={idx} severity="warning" sx={{ mb: 1 }}>
                      {factor}
                    </Alert>
                  ))
                ) : (
                  <Typography color="textSecondary">No significant risk factors identified</Typography>
                )}

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>✅ Protective Factors</Typography>
                {prediction.protectiveFactors.length > 0 ? (
                  prediction.protectiveFactors.map((factor, idx) => (
                    <Alert key={idx} severity="success" sx={{ mb: 1 }}>
                      {factor}
                    </Alert>
                  ))
                ) : (
                  <Typography color="textSecondary">No protective factors identified</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Input Features</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Feature</strong></TableCell>
                        <TableCell align="right"><strong>Value</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Company Age</TableCell>
                        <TableCell align="right">{prediction.features.companyAge.toFixed(1)} years</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>TechImpactScore</TableCell>
                        <TableCell align="right">{prediction.features.techImpactScore.toFixed(2)} / 10</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Degree Centrality</TableCell>
                        <TableCell align="right">{prediction.features.degreeCentrality.toFixed(3)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Cluster Archetype</TableCell>
                        <TableCell align="right">{prediction.features.clusterArchetype} ({prediction.features.clusterTag})</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Cluster Acquisition Rate</TableCell>
                        <TableCell align="right">{(prediction.features.clusterAcquisitionRate * 100).toFixed(0)}%</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Current Wave Intensity</TableCell>
                        <TableCell align="right">{prediction.features.currentWaveIntensity.toFixed(2)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Market Concentration (HHI)</TableCell>
                        <TableCell align="right">{prediction.features.marketConcentrationHHI.toFixed(0)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {}
      {activeTab === 1 && prediction && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📈 Kaplan-Meier Survival Curve
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              S(t) = P(T &gt; t | X) - Probability company survives beyond time t
            </Typography>

            <ResponsiveContainer width="100%" height={400}>
              <AreaChart data={prediction.survivalCurve}>
                <defs>
                  <linearGradient id="colorSurvival" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timeYears"
                  label={{ value: 'Time (Years)', position: 'insideBottom', offset: -5 }}
                />
                <YAxis
                  label={{ value: 'Survival Probability', angle: -90, position: 'insideLeft' }}
                  domain={[0, 1]}
                />
                <Tooltip
                  formatter={(value) => (value * 100).toFixed(1) + '%'}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="survivalProbability"
                  stroke="#8884d8"
                  fillOpacity={1}
                  fill="url(#colorSurvival)"
                  name="Survival Probability"
                />
                <Area
                  type="monotone"
                  dataKey="confidenceIntervalUpper"
                  stroke="#82ca9d"
                  fill="none"
                  strokeDasharray="5 5"
                  name="95% CI Upper"
                />
                <Area
                  type="monotone"
                  dataKey="confidenceIntervalLower"
                  stroke="#82ca9d"
                  fill="none"
                  strokeDasharray="5 5"
                  name="95% CI Lower"
                />
              </AreaChart>
            </ResponsiveContainer>

            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>Mathematical Model:</strong> Exponential survival function S(t) = exp(-λt) where λ is the hazard rate
              derived from Cox Proportional Hazards model with features: company age, TechScore, network centrality, cluster, and wave intensity.
            </Alert>
          </CardContent>
        </Card>
      )}

      {}
      {activeTab === 2 && prediction && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              🎯 Feature Importance (SHAP Values)
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Impact of each feature on survival probability (positive = increases survival, negative = decreases survival)
            </Typography>

            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={Object.entries(prediction.featureImportance).map(([key, value]) => ({
                  feature: key,
                  importance: value
                }))}
                layout="vertical"
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" label={{ value: 'Impact on Survival Probability', position: 'insideBottom', offset: -5 }} />
                <YAxis type="category" dataKey="feature" width={150} />
                <Tooltip formatter={(value) => value.toFixed(3)} />
                <Bar dataKey="importance" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>

            {modelMetrics && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>Model Feature Importance Rankings</Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={Object.entries(modelMetrics.featureImportanceScores).map(([key, value]) => ({
                    feature: key,
                    importance: value
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="feature" angle={-45} textAnchor="end" height={100} />
                    <YAxis label={{ value: 'Importance Score', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Bar dataKey="importance" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {}
      {activeTab === 3 && modelMetrics && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>📊 Model Validation Metrics</Typography>

                <Box sx={{ my: 2 }}>
                  <Typography variant="body2" color="textSecondary">Concordance Index (C-Index)</Typography>
                  <Typography variant="h4">{modelMetrics.concordanceIndex.toFixed(3)}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Analogous to ROC-AUC for survival models (&gt; 0.75 = good)
                  </Typography>
                </Box>

                <Box sx={{ my: 2 }}>
                  <Typography variant="body2" color="textSecondary">Brier Score</Typography>
                  <Typography variant="h4">{modelMetrics.brierScore.toFixed(3)}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Prediction error (&lt; 0.2 = acceptable)
                  </Typography>
                </Box>

                <Box sx={{ my: 2 }}>
                  <Typography variant="body2" color="textSecondary">Mean Absolute Error</Typography>
                  <Typography variant="h4">{modelMetrics.meanAbsoluteError.toFixed(1)} years</Typography>
                </Box>

                <Box sx={{ my: 2 }}>
                  <Typography variant="body2" color="textSecondary">RMSE</Typography>
                  <Typography variant="h4">{modelMetrics.rootMeanSquaredError.toFixed(1)} years</Typography>
                </Box>

                <Box sx={{ my: 2 }}>
                  <Typography variant="body2" color="textSecondary">Training/Test Split</Typography>
                  <Typography variant="body1">
                    Train: {modelMetrics.trainingSampleSize} | Test: {modelMetrics.testSampleSize}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Time-Dependent ROC-AUC</Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Model discrimination at different time horizons
                </Typography>

                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={Object.entries(modelMetrics.timeDependentAUC).map(([time, auc]) => ({
                    time: parseInt(time),
                    auc: auc
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" label={{ value: 'Time (Years)', position: 'insideBottom', offset: -5 }} />
                    <YAxis domain={[0.5, 1]} label={{ value: 'AUC', angle: -90, position: 'insideLeft' }} />
                    <Tooltip formatter={(value) => value.toFixed(3)} />
                    <Legend />
                    <Line type="monotone" dataKey="auc" stroke="#8884d8" strokeWidth={2} name="Time-Dependent AUC" />
                  </LineChart>
                </ResponsiveContainer>

                <Alert severity="success" sx={{ mt: 2 }}>
                  <strong>Performance Assessment:</strong> C-Index of {modelMetrics.concordanceIndex.toFixed(3)} indicates
                  the model correctly ranks {(modelMetrics.concordanceIndex * 100).toFixed(1)}% of company pairs by survival time.
                </Alert>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>📝 Model Information</Typography>
                <Typography variant="body2"><strong>Model Version:</strong> {modelMetrics.modelVersion}</Typography>
                <Typography variant="body2"><strong>Training Date:</strong> {new Date(modelMetrics.modelTrainedAt).toLocaleString()}</Typography>
                <Typography variant="body2"><strong>Cross-Validation Folds:</strong> {modelMetrics.cvFolds}</Typography>
                <Typography variant="body2"><strong>Calibration Slope:</strong> {modelMetrics.calibrationSlope.toFixed(3)} (ideal = 1.0)</Typography>
                <Typography variant="body2"><strong>Calibration Intercept:</strong> {modelMetrics.calibrationIntercept.toFixed(3)} (ideal = 0.0)</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {}
      {activeTab === 4 && historicalValidation && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              🔍 Historical Prediction Validation (2015-2020)
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Comparing predicted outcomes against actual M&A events
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography color="textSecondary">Total Cases</Typography>
                  <Typography variant="h4">{historicalValidation.totalCases}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', background: '#e8f5e9' }}>
                  <Typography color="textSecondary">Correct Predictions</Typography>
                  <Typography variant="h4" sx={{ color: '#388e3c' }}>{historicalValidation.correctPredictions}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography color="textSecondary">Accuracy (±1 year)</Typography>
                  <Typography variant="h4">{(historicalValidation.accuracy * 100).toFixed(1)}%</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography color="textSecondary">Mean Absolute Error</Typography>
                  <Typography variant="h4">{historicalValidation.meanAbsoluteError.toFixed(1)} yrs</Typography>
                </Paper>
              </Grid>
            </Grid>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Company</strong></TableCell>
                    <TableCell align="center"><strong>Predicted Time</strong></TableCell>
                    <TableCell align="center"><strong>Actual Time</strong></TableCell>
                    <TableCell align="center"><strong>Error (years)</strong></TableCell>
                    <TableCell align="center"><strong>Correct?</strong></TableCell>
                    <TableCell align="center"><strong>Acquisition Year</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historicalValidation.results.slice(0, 10).map((result, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{result.companyName}</TableCell>
                      <TableCell align="center">{result.predictedTimeToAcquisition.toFixed(1)}</TableCell>
                      <TableCell align="center">{result.actualTimeToAcquisition?.toFixed(1) || 'N/A'}</TableCell>
                      <TableCell align="center">{result.predictionError?.toFixed(1) || 'N/A'}</TableCell>
                      <TableCell align="center">
                        {result.predictionCorrect ?
                          <Chip label="✓" color="success" size="small" /> :
                          <Chip label="✗" color="error" size="small" />
                        }
                      </TableCell>
                      <TableCell align="center">{result.acquisitionYear}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>Validation Methodology:</strong> Predictions were simulated using data available 2 years before
              each acquisition event (2015-2020 period). Accuracy measured with ±1 year tolerance.
            </Alert>
          </CardContent>
        </Card>
      )}

    </Box>
  );
};

export default ViabilityPredictionDashboard;
